// Libraries
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Services
import { ZoneService } from 'src/app/services/api/zones/zone.service';

// Interfaces
import { Zone } from 'src/app/interfaces/api/zone';
import { Config } from 'src/app/interfaces/shared/config';

// Classes
import { Tools } from 'src/app/classes/shared/tools';

// UI
import { ConfirmComponent } from 'projects/ui-library/src/lib/components/confirm/confirm.component';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, SearchBarComponent } from 'ui-library';

@Component({
  selector: 'app-zones-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatPaginatorModule,
    MatTableModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    ButtonComponent,
    FormsModule,
    TranslateModule,
    SearchBarComponent,
    NoDataComponent
  ],
  standalone: true,
  templateUrl: './zones-dialog.component.html',
  styleUrls: ['./zones-dialog.component.scss']
})

export class ZonesDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  // Services
  private readonly zoneService: ZoneService = inject(ZoneService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly dialogRef: MatDialogRef<ZonesDialogComponent> = inject(MatDialogRef<ZonesDialogComponent>);
  private readonly tools: Tools = inject(Tools);
  public readonly data: any = inject(MAT_DIALOG_DATA);

  // Components API
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  // Variables
  displayedColumns: string[] = ['display', 'name', 'color', 'opacity', 'z', 'height', 'actions'];
  dataSource: any;
  onAdd = new EventEmitter();
  enableAllzones = false;
  isModifyZone = false;
  zoneId: any;
  editedZoneId!: string;
  editZoneEnabled = false;
  zoneForm: any;
  colors: any[] = [];
  zone: any;
  destroy$ = new Subject<void>();
  validSearchText: string = "";
  acceptedColors = ["primary", "danger", "success", "info", "warning", "lemon", "teal", "fuchsia", "dark", "info", "white"]
  listColor: { name: string; value: string }[] = [];

  constructor() {
    this.dataSource = new MatTableDataSource<any>(this.data.zones);

    this.translate.get('lodaded').pipe(takeUntil(this.destroy$)).subscribe(() => { });
    this.colors = [
      { title: `Danger(${this.translate.instant('colors.red')})`, value: 'danger' },
      { title: `Warning(${this.translate.instant('colors.orange')})`, value: 'warning' },
      { title: `Primary(${this.translate.instant('colors.blue')})`, value: 'primary' },
      { title: `Success(${this.translate.instant('colors.green')})`, value: 'success' },
      { title: `Info(${this.translate.instant('colors.lightGrey')})`, value: 'info' },
      { title: `Dark(${this.translate.instant('colors.black')})`, value: 'dark' },
      { title: `Muted(${this.translate.instant('colors.grey')})`, value: 'muted' },
    ]

    this.listColor = Object.entries(Config.colors)
      .filter(([name]) => this.acceptedColors.includes(name))
      .map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value
      }));
  }

  // Angular Lifecycle Hooks

  ngOnInit(): void {
    this.showCheckedBox();
    this.zoneForm = this.fb.group({
      name: ['',],
      color: ['',],
      opacity: ['',],
      z: [0,],
      height: [0,]
    })
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    this.validSearchText = text.trim().toLowerCase();
    this.dataSource.filter = this.validSearchText;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  deleteZone(zone: any) {
    const trad = {
      title: this.translate.instant('form.delete') + " " + zone.name,
      content: this.translate.instant('viewer.zone.delete') + " " + zone.name + " ?"
    }

    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '300px',
      data: {
        title: trad.title,
        content: trad.content
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.ok) {
        this.zoneService.removeZone(zone.id).pipe(takeUntil(this.destroy$)).subscribe(res => {
          this.zoneService.getZones().pipe(takeUntil(this.destroy$)).subscribe(res => {
            this.dataSource = new MatTableDataSource<any>(res);
            this.dataSource.paginator = this.paginator;
            this.data.zones = res;
            this.onAdd.emit({ zones: this.data.zones });
            this.showCheckedBox()
          }, err => console.error(err))
        }, err => console.error(err))
      }
    });
  }

  showCheckedBox() {
    let found = true;

    this.data.zones.forEach((t: Zone) => {
      if (!t.visible)
        found = false
    })

    found ? this.enableAllzones = true : this.enableAllzones = false;

    if (this.data.zones.length == 0)
      this.enableAllzones = false
  }

  createZone() {
    this.dialogRef.close({ createZone: true, editZone: false })
  }

  editZone(zone: Zone | null = null) {

    this.data.zones.forEach((z: Zone) => {
      if (zone && z.name == zone.name)
        z.edited = true;
      else
        z.edited = false;
    })

    if (zone) {
      this.dialogRef.close({ editZone: true, zone })
    }
  }

  showAllZones(event: any) {
    if (event.checked) {
      this.data.zones.forEach((t: Zone) => t.visible = true);
    } else {
      this.data.zones.forEach((t: Zone) => t.visible = false)
    }

    this.onAdd.emit({ zones: this.data.zones });
    this.data.zones.forEach((t: Zone) => this.updateZone(t))
  }

  showZone(event: any, zone: Zone) {
    if (event.checked) {
      this.data.zones.forEach((t: Zone) => {
        if (t.id == zone.id)
          t.visible = true
      })
    } else {
      this.data.zones.forEach((t: Zone) => {
        if (t.id == zone.id)
          t.visible = false
      })
    }

    this.showCheckedBox();
    this.onAdd.emit({ zones: this.data.zones });

    const findIndex = this.data.zones.findIndex((t: Zone) => t.id == zone.id);
    this.updateZone(this.data.zones[findIndex])
  }

  updateZone(zone: Zone) {
    let zoneToSave = { ...zone };
    delete zoneToSave.polygonLatLon;
    delete zoneToSave.polygonObj;

    this.zoneService.updateZone(zoneToSave).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        this.isModifyZone = false;
        this.zoneId = null;
      },
      error: (err) => console.error(err)
    })
  }

  modifyZone(zone: Zone) {
    this.zoneForm.patchValue({ name: zone.name, color: zone.color, opacity: zone.alpha, z: zone.z, height: zone.height })
    this.isModifyZone = true;
    this.zoneId = zone.id;
    this.zone = zone;
  }

  setChangesofZone(zone: Zone) {
    this.zone.name = this.zoneForm.value.name;
    this.zone.color = this.zoneForm.value.color;
    this.zone.alpha = this.zoneForm.value.opacity;
    this.zone.z = this.zoneForm.value.z;
    this.zone.height = this.zoneForm.value.height;
    this.updateZone(this.zone)
  }

  duplicateZone(zone: Zone) {
    this.dialogRef.close({ duplicateZone: true, zone })
  }

  getColorValue(colorKey: string | null | undefined): string {
    return this.tools.getColorValue(colorKey, this.listColor);
  }

  getColorName(colorKey: string | null | undefined): string {
    return this.tools.getColorName(colorKey, this.listColor);
  }

  close() {
    this.dialogRef.close({})
  }
}
